import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Overlay,
  Modal,
  Header,
  Title,
  CloseButton,
  Description,
  TotalsCard,
  TotalsRow,
  TotalsLabel,
  TotalsValue,
  InstallmentsSection,
  InstallmentCard,
  InstallmentHeader,
  InstallmentLabel,
  InstallmentAmount,
  InstallmentDaysOverdue,
  InstallmentDaysDue,
  StatusBadge,
  InstallmentDetails,
  DetailRow,
  DetailLabel,
  DetailValue,
  DetailHelper,
  ChevronButton,
  ShowAllButton
} from './styles'

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
})

const formatCurrency = value => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-'
  return currencyFormatter.format(value)
}

const formatDate = value => {
  if (!value) return '-'
  const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (parts) {
    const [, year, month, day] = parts
    return `${day}/${month}/${year}`
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR').format(parsed)
}

const statusMeta = {
  delayed: { label: 'Em atraso', variant: 'overdue' },
  overdue: { label: 'Em atraso', variant: 'overdue' },
  due: { label: 'A vencer', variant: 'upcoming' },
  upcoming: { label: 'A vencer', variant: 'upcoming' },
  paid: { label: 'Pago', variant: 'paid' }
}

const CalculationModal = ({ isOpen, onClose, calculation }) => {
  const [expandedId, setExpandedId] = useState(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setExpandedId(calculation?.installments?.[0]?.id || null)
    setShowAll(false)
  }, [calculation])

  const installments = useMemo(() => {
    if (!calculation?.installments) return []
    return calculation.installments
  }, [calculation])

  const visibleInstallments = useMemo(() => {
    if (showAll) return installments
    return installments.slice(0, 3)
  }, [installments, showAll])

  const handleToggleExpand = useCallback(id => {
    setExpandedId(prev => (prev === id ? null : id))
  }, [])

  const handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  if (!isOpen || !calculation) return null

  return (
    <Overlay onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <Modal>
        <Header>
          <Title>{calculation.title || 'Cálculo do saldo devedor'}</Title>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            ×
          </CloseButton>
        </Header>

        {calculation.description && <Description>{calculation.description}</Description>}

        <TotalsCard>
          <TotalsRow>
            <TotalsLabel>Valor em atraso (com encargos)</TotalsLabel>
            <TotalsValue>{formatCurrency(calculation.totals?.overdue)}</TotalsValue>
          </TotalsRow>
          <TotalsRow>
            <TotalsLabel>Valor futuro (com descontos)</TotalsLabel>
            <TotalsValue>{formatCurrency(calculation.totals?.future)}</TotalsValue>
          </TotalsRow>
          <TotalsRow $highlight>
            <TotalsLabel>Saldo total devedor</TotalsLabel>
            <TotalsValue>{formatCurrency(calculation.totals?.total)}</TotalsValue>
          </TotalsRow>
        </TotalsCard>

        <InstallmentsSection>
          <div className="section-header">
            <span>Detalhamento das parcelas</span>
            {installments.length > 3 && (
              <ShowAllButton type="button" onClick={() => setShowAll(prev => !prev)}>
                {showAll ? 'Mostrar menos' : 'Mostrar todos'}
              </ShowAllButton>
            )}
          </div>

          {visibleInstallments.map(installment => {
            const meta = statusMeta[installment.status] || statusMeta.upcoming
            const isExpanded = expandedId === installment.id
            const details = installment.details || {}
            const isDue = installment.status === 'due'

            return (
              <InstallmentCard key={installment.id}>
                <InstallmentHeader>
                  <div>
                    <InstallmentLabel>{installment.label}</InstallmentLabel>
                    <StatusBadge $variant={meta.variant}>{meta.label}</StatusBadge>
                  </div>
                  <InstallmentAmount>{formatCurrency(installment.amount)}</InstallmentAmount>
                  {isDue && details.daysLate != null ? (
                    <InstallmentDaysDue>{Math.abs(details.daysLate)} dias para vencer</InstallmentDaysDue>
                  ) : details.daysOverdue ? (
                    <InstallmentDaysOverdue>{details.daysOverdue} dias de atraso</InstallmentDaysOverdue>
                  ) : null}
                  <ChevronButton
                    type="button"
                    aria-label={isExpanded ? 'Recolher detalhes' : 'Expandir detalhes'}
                    onClick={() => handleToggleExpand(installment.id)}
                    $expanded={isExpanded}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ChevronButton>
                </InstallmentHeader>
                {isExpanded && (
                  <InstallmentDetails>
                    <DetailRow>
                      <DetailLabel>Vencimento original</DetailLabel>
                      <DetailValue>{formatDate(details.dueDate)}</DetailValue>
                    </DetailRow>
                    {details.originalValue !== undefined && (
                      <DetailRow>
                        <DetailLabel>Valor original</DetailLabel>
                        <DetailValue>{formatCurrency(details.originalValue)}</DetailValue>
                      </DetailRow>
                    )}
                    {isDue ? (
                      details.presentValueDiscount !== undefined && (
                        <DetailRow>
                          <DetailLabel>
                            Desconto (valor presente)
                            {details.remuneratoryRate && <DetailHelper>{details.remuneratoryRate}</DetailHelper>}
                          </DetailLabel>
                          <DetailValue>{formatCurrency(-Math.abs(details.presentValueDiscount))}</DetailValue>
                        </DetailRow>
                      )
                    ) : (
                      <>
                        {details.remuneratoryInterest !== undefined && (
                          <DetailRow>
                            <DetailLabel>
                              Juros remuneratórios
                              {details.remuneratoryRate && <DetailHelper>{details.remuneratoryRate}</DetailHelper>}
                            </DetailLabel>
                            <DetailValue>{formatCurrency(details.remuneratoryInterest)}</DetailValue>
                          </DetailRow>
                        )}
                        {details.defaultInterest !== undefined && (
                          <DetailRow>
                            <DetailLabel>Juros moratórios</DetailLabel>
                            <DetailValue>{formatCurrency(details.defaultInterest)}</DetailValue>
                          </DetailRow>
                        )}
                        {details.fine !== undefined && (
                          <DetailRow>
                            <DetailLabel>Multa</DetailLabel>
                            <DetailValue>{formatCurrency(details.fine)}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    )}
                  </InstallmentDetails>
                )}
              </InstallmentCard>
            )
          })}
        </InstallmentsSection>
      </Modal>
    </Overlay>
  )
}

CalculationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  calculation: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    totals: PropTypes.shape({
      overdue: PropTypes.number,
      future: PropTypes.number,
      total: PropTypes.number
    }),
    installments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        status: PropTypes.string,
        amount: PropTypes.number,
        details: PropTypes.object
      })
    )
  })
}

export default CalculationModal
